import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import sortBy from "lodash/sortBy";
import { useSnackbar } from "notistack";
import type {
  CancellationCondition,
  CreateCancellationConditionRequest,
  UpdateCancellationConditionRequest,
} from "./cancellationConditionsApi";
import {
  addCancellationCondition,
  deleteCancellationCondition,
  listCancellationConditions,
  updateCancellationCondition,
} from "./cancellationConditionsApi";

const cancellationConditionsKey = "cancellation-conditions-dictionary";

export const useCancellationConditions = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: [cancellationConditionsKey],
    queryFn: ({ signal }) => listCancellationConditions(signal),
  });

  const addMutation = useMutation<CancellationCondition, unknown, CreateCancellationConditionRequest>({
    mutationFn: (body) => addCancellationCondition(body),
    onSuccess: async (data) => {
      enqueueSnackbar(`Warunek anulacji '${data.shortName}' został dodany do słownika`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [cancellationConditionsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać warunku anulacji do słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateMutation = useMutation<
    CancellationCondition,
    unknown,
    { id: string; request: UpdateCancellationConditionRequest }
  >({
    mutationFn: ({ id, request }) => updateCancellationCondition(id, request),
    onSuccess: async (data) => {
      enqueueSnackbar(`Warunek anulacji '${data.shortName}' został zaktualizowany`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [cancellationConditionsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować warunku anulacji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation<void, unknown, string>({
    mutationFn: (id) => deleteCancellationCondition(id),
    onSuccess: async () => {
      enqueueSnackbar("Warunek anulacji został usunięty ze słownika", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [cancellationConditionsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć warunku anulacji ze słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    conditions: sortBy(data ?? [], ({ shortName }) => shortName.toLowerCase()),
    isLoading,
    addCondition: (body: CreateCancellationConditionRequest) => addMutation.mutateAsync(body),
    updateCondition: (id: string, request: UpdateCancellationConditionRequest) =>
      updateMutation.mutateAsync({ id, request }),
    deleteCondition: (id: string) => deleteMutation.mutateAsync(id),
  };
};
